import type { GhostAppearance } from '../../shared/types/AppearanceTypes';

/**
 * AppearanceHash — Shared utility for generating deterministic hashes
 * from character appearance data.
 *
 * Used by both client (texture cache keys) and server (appearance comparison).
 * No Phaser or browser dependencies — pure logic.
 */
export class AppearanceHash {
  /**
   * Generate a deterministic hash string from a GhostAppearance.
   * Uses djb2 algorithm for fast, low-collision hashing.
   */
  static generate(appearance: GhostAppearance): string {
    const canonical = AppearanceHash.canonicalize(appearance);
    return AppearanceHash.djb2(canonical);
  }

  /**
   * Generate a human-readable debug key for an appearance.
   * Useful for dev tools and logging.
   * Example: "wht-rnd-sml-sword-plate"
   */
  static generateDebugKey(appearance: GhostAppearance): string {
    const parts: string[] = [
      appearance.color?.slice(1, 4) || 'fff',
      appearance.eyes?.slice(0, 3) || 'rnd',
      appearance.shape?.slice(0, 3) || 'cls',
    ];

    if (appearance.equipmentVisuals) {
      parts.push('eqvis');
    } else {
      if (appearance.weapon) {
        parts.push(appearance.weapon.type);
      }
      if (appearance.equipment) {
        parts.push(appearance.equipment.type);
      }
    }
    if (appearance.aura && appearance.aura !== 'none') {
      parts.push(appearance.aura.slice(0, 3));
    }

    return parts.join('-');
  }

  /**
   * Check if two appearances are visually identical (same hash).
   */
  static areEqual(a: GhostAppearance, b: GhostAppearance): boolean {
    return AppearanceHash.generate(a) === AppearanceHash.generate(b);
  }

  /**
   * Create a canonical JSON string from an appearance for deterministic hashing.
   * Keys are sorted to ensure order-independent comparison.
   *
   * In authoritative equipmentVisuals mode (BIG-EQUIP-VIS-01 S2), legacy
   * appearance.weapon and appearance.equipment do not affect rendered output
   * and therefore must not produce different texture hashes. They are stripped
   * from the canonical form when equipmentVisuals is present. When absent,
   * legacy hashing is preserved exactly as before.
   */
  private static canonicalize(appearance: GhostAppearance): string {
    const source = typeof (appearance as GhostAppearance & { toJSON?: () => unknown }).toJSON === 'function'
      ? (appearance as GhostAppearance & { toJSON: () => unknown }).toJSON()
      : appearance;
    // In authoritative mode, legacy weapon/equipment are not rendered and
    // must not affect the hash.
    const hasEquipmentVisuals = !!(source as Record<string, unknown>).equipmentVisuals;
    const toSort = hasEquipmentVisuals
      ? AppearanceHash.stripLegacyEquipment(source as Record<string, unknown>)
      : source;
    const sorted = AppearanceHash.sortObject(toSort);
    return JSON.stringify(sorted);
  }

  /**
   * Remove legacy `weapon` and `equipment` keys from the source object.
   * Used when equipmentVisuals is present so legacy fields don't affect the hash.
   */
  private static stripLegacyEquipment(source: Record<string, unknown>): Record<string, unknown> {
    const { weapon, equipment, ...rest } = source;
    void weapon; void equipment;
    return { ...rest, equipmentVisuals: AppearanceHash.drawnSlots(rest) };
  }

  /**
   * The equipment slots that draw on the ghost texture (lane BO). The Lantern
   * (`charm`) and the Flame (`halo`) are Pixi glows, never SVG layers (GhostLanternFx), and a
   * footless ghost (every player since lane BC) never draws the `feet` layer —
   * that slot styles the Pixi Trail (lane BD). Neither may change the texture
   * key, or a lantern or Trail swap regenerates an identical texture.
   */
  private static drawnSlots(source: Record<string, unknown>): Record<string, unknown> {
    const { charm, halo, feet, ...drawn } = source.equipmentVisuals as Record<string, unknown>;
    void charm; void halo;
    return source.footless === true ? drawn : { ...drawn, feet };
  }

  /**
   * Recursively sort object keys for deterministic serialization.
   */
  private static sortObject(obj: any): any {
    if (obj === null || obj === undefined) return obj;
    if (typeof obj !== 'object') return obj;
    if (Array.isArray(obj)) return obj.map(v => AppearanceHash.sortObject(v));

    const sorted: Record<string, any> = {};
    const keys = Object.keys(obj).sort();
    for (const key of keys) {
      const val = obj[key];
      // Skip undefined values for consistency
      if (val !== undefined) {
        sorted[key] = AppearanceHash.sortObject(val);
      }
    }
    return sorted;
  }

  /**
   * djb2 hash algorithm — fast, simple, good distribution.
   * Returns hex string.
   */
  private static djb2(str: string): string {
    let hash = 5381;
    for (let i = 0; i < str.length; i++) {
      hash = ((hash << 5) + hash + str.charCodeAt(i)) & 0xffffffff;
    }
    // Convert to unsigned 32-bit hex
    return (hash >>> 0).toString(16).padStart(8, '0');
  }
}
